/**
 * Example auth api connector that lets your user log in using a defined payload
 * and returns a promise
 *
 *
 */

import catchErrors from './catchErrors'
import { pusherService } from '@/services/pusherService'

const AGENT_SLOT_INTERACTION_TYPE_ONE_SHOT = 'one-shot'
const AGENT_SLOT_INTERACTION_TYPE_CONVERSATION = 'conversation'

const AGENT_SLOT_STORY_ENHANCEMENT = 'story_enhancement'
const AGENT_SLOT_STORY_WEIGHT = 'story_weight'
const AGENT_SLOT_SEQUENCE_MANAGEMENT = 'sequence_management'
const AGENT_SLOT_DIGITAL_GENERATOR = 'digital_generator'
const AGENT_SLOT_DIGITAL_GENERATOR_ONE_SHOT = 'digital_generator_one_shot'
const AGENT_SLOT_BROADCAST_CONVERSION = 'broadcast_conversion'
const AGENT_SLOT_DIGITAL_CONVERSION = 'digital_conversion'
const AGENT_SLOT_EDITORIAL_REVIEW = 'editorial_review_assistant'

// similar config context present on the api side - app/AI/Enums/AgentSlot.php
const AGENT_SLOTS = {
  [AGENT_SLOT_STORY_ENHANCEMENT]: {
    key: AGENT_SLOT_STORY_ENHANCEMENT,
    required: ['story_id'],
    mapParams: (params) => ({
      story_id: params.story_id
    }),
    interactionType: AGENT_SLOT_INTERACTION_TYPE_CONVERSATION,
    isStreaming: true,
    actionTypes: [{ type: 'apply', label: 'Apply' }]
  },

  [AGENT_SLOT_STORY_WEIGHT]: {
    key: AGENT_SLOT_STORY_WEIGHT,
    required: ['story_id'],
    mapParams: (params) => ({
      story_id: params.story_id
    }),
    isStreaming: false,
    interactionType: AGENT_SLOT_INTERACTION_TYPE_ONE_SHOT
  },

  [AGENT_SLOT_SEQUENCE_MANAGEMENT]: {
    key: AGENT_SLOT_SEQUENCE_MANAGEMENT,
    required: [],
    mapParams: (params) => ({
      sequence_id: params?.sequence_id || 0
    }),
    isStreaming: false,
    interactionType: AGENT_SLOT_INTERACTION_TYPE_ONE_SHOT
  },

  [AGENT_SLOT_DIGITAL_GENERATOR]: {
    key: AGENT_SLOT_DIGITAL_GENERATOR,
    required: ['script_id'],
    mapParams: (params) => ({
      script_id: params.script_id
    }),
    interactionType: AGENT_SLOT_INTERACTION_TYPE_CONVERSATION,
    isStreaming: true,
    actionTypes: [
      { type: 'apply_digital_generator_all', label: 'Apply All', mode: 'all' },
      { type: 'apply_digital_generator_digital', label: 'Fill for Website (Brightspot)' },
      { type: 'apply_digital_generator_social_facebook', label: 'Fill for Facebook' },
      { type: 'apply_digital_generator_social_twitter', label: 'Fill for Twitter/X' },
      { type: 'apply_digital_generator_social_instagram', label: 'Fill for Instagram' },
      { type: 'apply_digital_generator_social_youtube', label: 'Fill for YouTube' },
      { type: 'apply_digital_generator_social_linkedin', label: 'Fill for LinkedIn' }
    ]
  },

  [AGENT_SLOT_DIGITAL_GENERATOR_ONE_SHOT]: {
    key: AGENT_SLOT_DIGITAL_GENERATOR_ONE_SHOT,
    required: ['script_id'],
    mapParams: (params) => ({
      script_id: params.script_id
    }),
    isStreaming: false,
    interactionType: AGENT_SLOT_INTERACTION_TYPE_ONE_SHOT
  },

  [AGENT_SLOT_BROADCAST_CONVERSION]: {
    key: AGENT_SLOT_BROADCAST_CONVERSION,
    required: ['story_id'],
    mapParams: (params) => ({
      story_id: params.story_id
    }),
    interactionType: AGENT_SLOT_INTERACTION_TYPE_CONVERSATION,
    actionTypes: [{ type: 'apply', label: 'Apply' }]
  },

  [AGENT_SLOT_DIGITAL_CONVERSION]: {
    key: AGENT_SLOT_DIGITAL_CONVERSION,
    required: ['story_id'],
    mapParams: (params) => ({
      story_id: params.story_id
    }),
    interactionType: AGENT_SLOT_INTERACTION_TYPE_CONVERSATION,
    actionTypes: [{ type: 'apply', label: 'Apply' }]
  },

  [AGENT_SLOT_EDITORIAL_REVIEW]: {
    key: AGENT_SLOT_EDITORIAL_REVIEW,
    required: ['script_id'],
    mapParams: (params) => ({
      script_id: params.script_id
    }),
    interactionType: AGENT_SLOT_INTERACTION_TYPE_CONVERSATION,
    actionTypes: []
  }
}

const AGENT_SLOTS_KEYS = {
  AGENT_SLOT_STORY_ENHANCEMENT,
  AGENT_SLOT_STORY_WEIGHT,
  AGENT_SLOT_SEQUENCE_MANAGEMENT,
  AGENT_SLOT_DIGITAL_GENERATOR,
  AGENT_SLOT_DIGITAL_GENERATOR_ONE_SHOT,
  AGENT_SLOT_BROADCAST_CONVERSION,
  AGENT_SLOT_DIGITAL_CONVERSION,
  AGENT_SLOT_EDITORIAL_REVIEW
}

export default {
  AGENT_SLOTS,
  AGENT_SLOTS_KEYS,

  getSlotConfig(slot) {
    return AGENT_SLOTS[slot]
  },

  validateParams(slot, params = {}) {
    const config = AGENT_SLOTS[slot]

    if (!config) {
      throw new Error(`Unknown agent_slot: ${slot}`)
    }

    const missing = config.required.filter((key) => !params[key])

    if (missing.length) {
      throw new Error(`Missing params: ${missing.join(', ')}`)
    }
  },

  async startConversation(agent_slot, params = {}, { forceNew = false } = {}) {
    this.validateParams(agent_slot, params)

    const config = this.getSlotConfig(agent_slot)

    if (config.interactionType !== AGENT_SLOT_INTERACTION_TYPE_CONVERSATION) {
      throw new Error('Not allowed agent slot interaction type')
    }

    const payload = {
      agent_slot,
      ...config.mapParams(params),
      ...(forceNew ? { force_new: true } : {})
    }

    const res = await catchErrors.post(`/v2/newsdesk/ai/conversations/start`, payload)

    if (!res.data.success) {
      throw new Error(res.data.message)
    }

    return res.data.data
  },

  async getConversation(conversationId) {
    const res = await catchErrors.get(`/v2/newsdesk/ai/conversations/${conversationId}`)

    if (!res.data.success) {
      throw new Error(res.data.message)
    }

    return res.data.data
  },

  async getConversationContext(conversationId) {
    const res = await catchErrors.get(`/v2/newsdesk/ai/conversations/${conversationId}/context`)

    if (!res.data.success) {
      throw new Error(res.data.message)
    }

    return res.data.data
  },

  async sendMessage(conversationId, message) {
    const res = await catchErrors.post(`/v2/newsdesk/ai/conversations/${conversationId}/message`, { message })

    if (!res.data.success) {
      throw new Error(res.data.message)
    }

    return res.data.data
  },

  async streamMessage(conversationId, message) {
    const res = await catchErrors.post(`/v2/newsdesk/ai/conversations/${conversationId}/stream`, { message })

    if (!res.data.success) {
      throw new Error(res.data.message)
    }

    return res.data.data
  },

  async applyExecution(conversationId, executionId, actionType = null) {
    const res = await catchErrors.post(`/v2/newsdesk/ai/conversations/${conversationId}/execution/${executionId}/apply`, { actionType })

    if (!res.data.success) {
      throw new Error(res.data.message)
    }

    return res.data.data
  },

  async getOutputContext(conversationId, executionId) {
    const res = await catchErrors.get(`/v2/newsdesk/ai/conversations/${conversationId}/execution/${executionId}/output-context`)

    if (!res.data.success) {
      throw new Error(res.data.message)
    }

    return res.data.data
  },

  async applyMapped(conversationId, executionId, platforms) {
    const res = await catchErrors.post(`/v2/newsdesk/ai/conversations/${conversationId}/execution/${executionId}/apply-mapped`, { platforms })

    if (!res.data.success) {
      throw new Error(res.data.message)
    }

    return res.data.data
  },

  /**
   * One-shot execution (no conversation)
   */
  async oneShotExecute(agent_slot, params = {}) {
    this.validateParams(agent_slot, params)

    const config = this.getSlotConfig(agent_slot)

    if (config.interactionType !== AGENT_SLOT_INTERACTION_TYPE_ONE_SHOT) {
      throw new Error('Not allowed agent slot interaction type')
    }

    const payload = {
      agent_slot,
      ...config.mapParams(params)
    }

    const res = await catchErrors.post('/v2/newsdesk/ai/one-shot-execute', payload)

    if (!res.data.success) {
      throw new Error(res.data.message)
    }

    return res.data.data
  },

  /**
   * Subscribe to execution events (Reverb)
   */
  subscribeToOneShotExecution(executionId, handlers = {}) {
    console.log('[AI] Subscribing to execution:', executionId)
    const channelName = `private-agent-execution.one-shot.${executionId}`

    pusherService.subscribe(channelName)

    const cleanups = []

    const bind = (event, handler) => {
      if (!handler) return

      const unsubscribe = pusherService.on(channelName, event, handler)
      cleanups.push(unsubscribe)
    }

    // cleanup helper
    const unsubscribe = () => {
      cleanups.forEach((fn) => fn())
      pusherService.unsubscribe(channelName)
    }

    // lifecycle events
    bind('ai.agent_executor.completed', (data) => {
      console.log('[AI One-shot Execution] ai.agent_executor.completed', data)
      handlers.onCompleted && handlers.onCompleted(data)
      unsubscribe()
    })

    bind('ai.agent_executor.failed', (data) => {
      console.log('[AI One-shot Execution] ai.agent_executor.failed', data)
      handlers.onFailed && handlers.onFailed(data)
      unsubscribe()
    })

    return {
      unsubscribe
    }
  }
}
